/**
 * Post-upload notice logic, shared by the document / knowledge upload modals.
 *
 * A file upload has two independent outcomes: whether the file was *saved*
 * (the HTTP upload succeeded) and, for a saved file, whether the AI *index*
 * built successfully. A file can be saved yet fail to index (e.g. an engine
 * hiccup or an unsupported-but-accepted format) — in that case it is usable as
 * a stored document but cannot be queried in document Q&A, which the user must
 * be told. This module turns a batch of outcomes into the toast strings to
 * show, keeping the presentation-free decision testable in isolation.
 */

import { RAG_INDEX_STATUS, RAG_INDEX_FAILED_NOTICE } from "../constants";
import type { RagIndexStatus } from "../types";

export interface FileUploadOutcome {
  /** True when the file was persisted (the HTTP upload succeeded). */
  saved: boolean;
  /**
   * AI index status the server returned for a saved file. null / undefined
   * means the file was not submitted for indexing (e.g. an unsupported format)
   * — that is a normal, non-error state and raises no warning.
   */
  ragIndexStatus?: RagIndexStatus | null;
}

export interface UploadNotices {
  /** "N 个文件上传成功" — omitted when nothing was saved. */
  success?: string;
  /** "N 个文件上传失败" — omitted when nothing failed to save. */
  error?: string;
  /** AI index failure warning — present when >=1 saved file failed to index. */
  indexWarning?: string;
  /** How many files failed to save — callers branch on this (e.g. auto-close
   *  the modal on 0) instead of re-deriving it from the outcomes. */
  failedCount: number;
}

/**
 * Map Promise.allSettled results onto upload outcomes: a rejected promise is a
 * file that failed to save. Shared by the upload modals so the settlement rule
 * lives next to the notice rule.
 */
export function settleUploadOutcomes(
  results: PromiseSettledResult<FileUploadOutcome>[],
): FileUploadOutcome[] {
  return results.map((r) =>
    r.status === "fulfilled" ? r.value : { saved: false },
  );
}

/**
 * Decide which toast strings to show for a batch of upload outcomes.
 *
 * The index warning is independent of the success/error tallies: a file counts
 * toward "success" as soon as it is saved, and *additionally* raises the index
 * warning when its index build failed. A "pending" status is the normal state
 * right after upload (the engine indexes asynchronously) and raises nothing —
 * the per-document badge tracks it to ready/failed.
 */
export function buildUploadNotices(
  outcomes: FileUploadOutcome[],
): UploadNotices {
  const savedCount = outcomes.filter((o) => o.saved).length;
  const failedCount = outcomes.length - savedCount;
  const indexFailedCount = outcomes.filter(
    (o) => o.saved && o.ragIndexStatus === RAG_INDEX_STATUS.FAILED,
  ).length;

  const notices: UploadNotices = { failedCount };
  if (savedCount > 0) notices.success = `${savedCount} 个文件上传成功`;
  if (failedCount > 0) notices.error = `${failedCount} 个文件上传失败`;
  if (indexFailedCount > 0) notices.indexWarning = RAG_INDEX_FAILED_NOTICE;
  return notices;
}
